import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { UniversalLink } from '@plone/volto/components';
import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';
import { ALTURA, ESTADOS, LARGURA } from './brasil';
import {
  afastar,
  categoriasDe,
  circulosDe,
  CORES,
  descricao,
  pontosDe,
  unidadeDe,
} from './geo';

/**
 * Para que lado a dica cresce a partir do ponto. Perto da borda, centralizada
 * sobre o ponto, ela não tinha espaço e espremia o nome da cidade letra por
 * letra ("Jun/di/á"); ali ela se alinha para dentro do mapa.
 */
export const ladoDaDica = (x, largura) => {
  if (!largura) return 'meio';
  if (x < largura * 0.3) return 'esq';
  if (x > largura * 0.7) return 'dir';
  return 'meio';
};

/**
 * Mapa do Brasil: pontos (bases, escritórios, unidades) ou um valor por estado
 * (círculo de área proporcional, com o número escrito).
 *
 *  - fundo azul: faixa de ponta a ponta com a padronagem da marca, painel e
 *    lista ao lado do mapa (no celular, o mapa vem logo depois da legenda);
 *  - fundo branco: cartão na coluna do texto, painel em cima.
 *
 * Os estados são um desenho só (SVG); pontos e rótulos ficam por cima, em
 * HTML, para o texto não encolher com o mapa. No navegador, os pontos que se
 * cobrem são afastados e o rótulo que esbarra noutro se esconde (o nome
 * continua na dica e na lista). Quem não vê o desenho recebe a leitura em
 * texto (`descricao`) e a lista.
 */

const corDoTexto = (cor) => (cor === 'amarelo' ? CORES.marinho : '#fff');

const cruzam = (a, b) =>
  a.left < b.right && b.left < a.right && a.top < b.bottom && b.top < a.bottom;

// Afasta os pontos em pixels (o tamanho do marcador não muda com o mapa) e
// acha um lado para cada rótulo: o de partida, o oposto, acima, abaixo. Se em
// nenhum ele fica livre de outro rótulo, de outro ponto e da borda, ele se
// esconde (o nome continua na dica e na lista).
const LADOS = ['direita', 'esquerda', 'acima', 'abaixo'];
export const ajustar = (area) => {
  if (!area || !area.clientWidth) return;
  const W = area.clientWidth;
  const H = area.clientHeight;
  const els = [...area.querySelectorAll('.ag-mapa__ponto')];
  const base = els.map((el) => ({
    x: (parseFloat(el.dataset.x) / 100) * W,
    y: (parseFloat(el.dataset.y) / 100) * H,
    r: el.classList.contains('ag-mapa__ponto--numero') ? 12 : 8,
  }));
  afastar(base, 2).forEach((q, i) => {
    els[i].style.left = `${(q.x / W) * 100}%`;
    els[i].style.top = `${(q.y / H) * 100}%`;
  });
  const quadro = area.getBoundingClientRect();
  const marcas = els.map((el) =>
    el.querySelector('.ag-mapa__marca').getBoundingClientRect(),
  );
  const vistos = [];
  const livre = (bx, i) =>
    bx.left >= quadro.left - 2 &&
    bx.right <= quadro.right + 2 &&
    bx.top >= quadro.top - 2 &&
    !vistos.some((v) => cruzam(bx, v)) &&
    !marcas.some((m, j) => j !== i && cruzam(bx, m));
  els
    .map((el, i) => ({ el, i, r: el.querySelector('.ag-mapa__rotulo') }))
    .filter((x) => x.r)
    .sort((a, b) => b.el.dataset.prioridade - a.el.dataset.prioridade)
    .forEach(({ el, i, r }) => {
      const inicial = el.dataset.lado || 'direita';
      const ordem = [
        inicial,
        inicial === 'direita' ? 'esquerda' : 'direita',
        'acima',
        'abaixo',
      ];
      r.classList.remove('ag-mapa__rotulo--oculto');
      const lado = ordem.find((l) => {
        LADOS.forEach((x) =>
          r.classList.toggle(`ag-mapa__rotulo--${x}`, x === l),
        );
        return livre(r.getBoundingClientRect(), i);
      });
      if (lado) {
        vistos.push(r.getBoundingClientRect());
      } else {
        LADOS.forEach((x) =>
          r.classList.toggle(`ag-mapa__rotulo--${x}`, x === inicial),
        );
        r.classList.add('ag-mapa__rotulo--oculto');
      }
    });
};

const Marca = ({ cat, n }) => (
  <span
    className={`ag-mapa__marca ag-mapa__marca--${cat.marcador}`}
    style={{
      '--ag-mapa-cor': CORES[cat.cor],
      '--ag-mapa-texto': corDoTexto(cat.cor),
    }}
  >
    {n}
  </span>
);

Marca.propTypes = { cat: PropTypes.object.isRequired, n: PropTypes.number };

const MapaView = ({ data = {} }) => {
  const area = useRef(null);
  const [ativo, setAtivo] = useState(null);
  const estados = data.modo === 'estados';
  const fundo = data.fundo === 'azul' ? 'azul' : 'branco';
  const numerar = !estados && !!data.numerar;
  const cats = categoriasDe(data);
  const pontos = estados ? [] : pontosDe(data);
  const circulos = estados ? circulosDe(data) : [];
  const mostrarLista = !estados && data.lista !== false && pontos.length > 0;
  const Nivel = data.nivel === 'h3' ? 'h3' : 'h2';
  const contagem = data.contagem !== false;
  const legenda = estados
    ? []
    : cats
        .filter((c) => c.nome)
        .map((c) => ({
          cat: c,
          n: pontos.filter((p) => p.cat.id === c.id).length,
        }));
  const chaveDosPontos = pontos.map((p) => `${p.id}:${p.x}:${p.y}`).join('|');

  useEffect(() => {
    const el = area.current;
    ajustar(el);
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => ajustar(el));
    ro.observe(el);
    return () => ro.disconnect();
  }, [chaveDosPontos, numerar]);

  const abrir = (id, titulo, texto) => (e) => {
    const alvo = area.current?.querySelector(`[data-ponto="${id}"]`);
    if (!alvo || !area.current) return;
    const q = area.current.getBoundingClientRect();
    const b = alvo.getBoundingClientRect();
    const x = b.left + b.width / 2 - q.left;
    setAtivo({
      id,
      titulo,
      texto,
      x,
      y: b.top - q.top,
      lado: ladoDaDica(x, q.width),
    });
  };
  const fechar = () => setAtivo(null);

  const classes = [
    'ag-mapa',
    `ag-mapa--${fundo}`,
    estados && 'ag-mapa--estados',
    numerar && 'ag-mapa--numerado',
  ]
    .filter(Boolean)
    .join(' ');

  const itemDaLista = (p) => {
    const corpo = (
      <>
        <strong>{p.cidade}</strong>
        {p.detalhe && <span className="ag-mapa__detalhe">{p.detalhe}</span>}
      </>
    );
    return (
      <li
        key={p.id}
        className={ativo?.id === p.id ? 'ag-mapa--ativo' : undefined}
        onMouseEnter={abrir(p.id, p.cidade, p.detalhe)}
        onMouseLeave={fechar}
        onFocus={abrir(p.id, p.cidade, p.detalhe)}
        onBlur={fechar}
      >
        <Marca cat={p.cat} n={numerar ? p.n : undefined} />
        <span>
          {p.link ? (
            <UniversalLink href={flattenToAppURL(p.link)}>
              {corpo}
            </UniversalLink>
          ) : (
            corpo
          )}
        </span>
      </li>
    );
  };

  return (
    <section className={classes}>
      <div className="ag-mapa__inner">
        <div className="ag-mapa__painel">
          {(data.etiqueta || '').trim() && (
            <span className="ag-mapa__etiqueta">{data.etiqueta.trim()}</span>
          )}
          {(data.titulo || '').trim() && (
            <Nivel className="ag-mapa__titulo">{data.titulo.trim()}</Nivel>
          )}
          {(data.subtitulo || '').trim() && (
            <p className="ag-mapa__sub">{data.subtitulo.trim()}</p>
          )}
          {legenda.length > 0 && (
            <ul className="ag-mapa__legenda">
              {legenda.map(({ cat, n }) => (
                <li key={cat.id}>
                  <Marca cat={cat} />
                  {cat.nome}
                  {contagem && n > 0 ? ` · ${n}` : ''}
                </li>
              ))}
            </ul>
          )}
        </div>
        <figure className="ag-mapa__figura">
          <div
            className="ag-mapa__area"
            ref={area}
            role="img"
            aria-label={descricao(data)}
            style={{ aspectRatio: `${LARGURA} / ${ALTURA}` }}
          >
            <svg
              className="ag-mapa__desenho"
              viewBox={`0 0 ${LARGURA} ${ALTURA}`}
              aria-hidden="true"
              focusable="false"
            >
              {ESTADOS.map((e) => (
                <path
                  key={e.uf}
                  className={
                    circulos.some((c) => c.uf === e.uf)
                      ? 'ag-mapa__uf ag-mapa__uf--tom'
                      : 'ag-mapa__uf'
                  }
                  d={e.d}
                />
              ))}
            </svg>
            {pontos.map((p) => {
              const ci = cats.findIndex((c) => c.id === p.cat.id);
              const Tag = p.link ? 'a' : 'span';
              return (
                <Tag
                  key={p.id}
                  href={p.link ? flattenToAppURL(p.link) : undefined}
                  tabIndex={p.link ? -1 : undefined}
                  aria-hidden="true"
                  className={[
                    'ag-mapa__ponto',
                    numerar && 'ag-mapa__ponto--numero',
                    ativo?.id === p.id && 'ag-mapa--ativo',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  data-ponto={p.id}
                  data-x={p.x}
                  data-y={p.y}
                  data-prioridade={cats.length - ci}
                  data-lado={p.x > 72 ? 'esquerda' : 'direita'}
                  style={{ left: `${p.x}%`, top: `${p.y}%` }}
                  onMouseEnter={abrir(p.id, p.cidade, p.detalhe)}
                  onMouseLeave={fechar}
                  onClick={
                    p.link ? undefined : abrir(p.id, p.cidade, p.detalhe)
                  }
                >
                  <Marca cat={p.cat} n={numerar ? p.n : undefined} />
                  {p.rotulo && !numerar && (
                    <span
                      className={`ag-mapa__rotulo ag-mapa__rotulo--${
                        p.x > 72 ? 'esquerda' : 'direita'
                      }`}
                    >
                      {p.nome}
                    </span>
                  )}
                </Tag>
              );
            })}
            {circulos.map((c) => (
              <span
                key={c.id}
                aria-hidden="true"
                className={`ag-mapa__circulo${
                  ativo?.id === c.id ? ' ag-mapa--ativo' : ''
                }`}
                data-ponto={c.id}
                style={{ left: `${c.x}%`, top: `${c.y}%`, width: `${c.d}%` }}
                onMouseEnter={abrir(
                  c.id,
                  c.nome,
                  `${c.valor} ${unidadeDe(data, c.valor)}`.trim(),
                )}
                onMouseLeave={fechar}
                onClick={abrir(
                  c.id,
                  c.nome,
                  `${c.valor} ${unidadeDe(data, c.valor)}`.trim(),
                )}
              >
                <strong>{c.valor}</strong>
                <span>{c.uf}</span>
              </span>
            ))}
            {ativo && (
              <div
                className={`ag-mapa__dica ag-mapa__dica--${ativo.lado || 'meio'}`}
                aria-hidden="true"
                style={{ left: ativo.x, top: ativo.y }}
              >
                <strong>{ativo.titulo}</strong>
                {ativo.texto}
              </div>
            )}
          </div>
          {(data.fonte || '').trim() && (
            <figcaption>{data.fonte.trim()}</figcaption>
          )}
        </figure>
        {(mostrarLista || (circulos.length > 0 && data.lista !== false)) && (
          <div className="ag-mapa__mais">
            {mostrarLista &&
              (numerar ? (
                <ol className="ag-mapa__lista">{pontos.map(itemDaLista)}</ol>
              ) : (
                <ul className="ag-mapa__lista">{pontos.map(itemDaLista)}</ul>
              ))}
            {circulos.length > 0 && data.lista !== false && (
              <details className="ag-mapa__numeros">
                <summary>Ver os números por estado</summary>
                <ul>
                  {circulos.map((c) => (
                    <li key={c.id}>
                      <span>{c.nome}</span>
                      <strong>{c.valor}</strong>
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

MapaView.propTypes = {
  data: PropTypes.shape({
    fundo: PropTypes.string,
    modo: PropTypes.string,
    etiqueta: PropTypes.string,
    titulo: PropTypes.string,
    subtitulo: PropTypes.string,
    fonte: PropTypes.string,
    nivel: PropTypes.string,
    lista: PropTypes.bool,
    numerar: PropTypes.bool,
    contagem: PropTypes.bool,
    categorias: PropTypes.array,
    pontos: PropTypes.array,
    valores: PropTypes.array,
    unidade: PropTypes.string,
    unidadeSingular: PropTypes.string,
  }),
};

export default MapaView;
